import type { JSX } from "react";
import { Button } from "@aws-amplify/ui-react";

// SVG path data to draw the various toolbar action icons
const ACTION_ICON_PATHS = {
  download:
    "M480-320 280-520l56-58 104 104v-326h80v326l104-104 56 58-200 200ZM240-160q-33 0-56.5-23.5T160-240v-120h80v120h480v-120h80v120q0 33-23.5 56.5T720-160H240Z",
  "upload-file":
    "M440-200h80v-167l64 64 56-57-160-160-160 160 57 56 63-63v167ZM240-80q-33 0-56.5-23.5T160-160v-640q0-33 23.5-56.5T240-880h320l240 240v480q0 33-23.5 56.5T720-80H240Zm280-520v-200H240v640h480v-440H520ZM240-800v200-200 640-640Z",
  "delete-file":
    "M240-800v200-200 640-9.5 9.5-640Zm0 720q-33 0-56.5-23.5T160-160v-640q0-33 23.5-56.5T240-880h320l240 240v174q-19-7-39-10.5t-41-3.5v-120H520v-200H240v640h254q8 23 20 43t28 37H240Zm396-20-56-56 84-84-84-84 56-56 84 84 84-84 56 56-83 84 83 84-56 56-84-83-84 83Z",
  "file-text":
    "M240-80q-33 0-56.5-23.5T160-160v-640q0-33 23.5-56.5T240-880h320l240 240v480q0 33-23.5 56.5T720-80H240Zm280-520v-200H240v640h480v-440H520ZM320-240h320v-80H320v80Zm0-120h320v-80H320v80Zm0-120h200v-80H320v80Zm-80 240v-640 640Z",
  refresh:
    "M480-160q-134 0-227-93t-93-227q0-134 93-227t227-93q69 0 132 28.5T720-690v-110h80v280H520v-80h168q-32-56-87.5-88T480-720q-100 0-170 70t-70 170q0 100 70 170t170 70q77 0 139-44t87-116h84q-28 106-114 173t-196 67Z",
  "chevron-left": "M560-240 320-480l240-240 56 56-184 184 184 184-56 56Z",
  "chevron-right": "M504-480 320-664l56-56 240 240-240 240-56-56 184-184Z",
} as const;

export type ToolbarIconName = keyof typeof ACTION_ICON_PATHS;

type ToolbarButtonProperties = {
  /** Names the action; the tooltip and accessible name for icon-only buttons. */
  readonly label: string;
  readonly iconName: ToolbarIconName;
  readonly isDisabled?: boolean;
  readonly onSelect: () => void;
};

export function ToolbarActionIcon({
  name,
}: {
  readonly name: ToolbarIconName;
}): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      height="24"
      viewBox="0 -960 960 960"
      width="24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d={ACTION_ICON_PATHS[name]} fill="currentColor" />
    </svg>
  );
}

export function ToolbarIconButton({
  label,
  iconName,
  isDisabled = false,
  onSelect,
}: ToolbarButtonProperties): JSX.Element {
  return (
    <Button
      aria-label={label}
      className="evido-listing__toolbar-action"
      isDisabled={isDisabled}
      onClick={onSelect}
      title={label}
    >
      <ToolbarActionIcon name={iconName} />
    </Button>
  );
}

export function ToolbarLabelledButton({
  label,
  iconName,
  isDisabled = false,
  onSelect,
}: ToolbarButtonProperties): JSX.Element {
  return (
    <Button
      className="evido-listing__toolbar-action evido-listing__toolbar-action--labelled"
      isDisabled={isDisabled}
      onClick={onSelect}
    >
      <ToolbarActionIcon name={iconName} />
      {label}
    </Button>
  );
}
